import React, { useMemo, useState } from 'react'
import { Card, Col, Row, Statistic, Tooltip } from 'antd'
import { FallOutlined, InfoCircleOutlined, RiseOutlined } from '@ant-design/icons'
import dayjs from 'dayjs'
import {
    Area,
    Bar,
    CartesianGrid,
    ComposedChart,
    Legend,
    Line,
    ResponsiveContainer,
    Tooltip as RechartsTooltip,
    XAxis,
    YAxis,
} from 'recharts'
import { useTranslation } from 'react-i18next'

import { BaseChartContainer } from '../BaseChartContainer/BaseChartContainer'
import {
    ChartViewSwitcher,
    type ChartViewMode,
} from '../ChartViewSwitcher/ChartViewSwitcher'
import {
    calculateStatistics,
    calculateTrend,
} from '../../../utils/chartDataUtils'
import { ChartTheme } from '../../../utils/chartTheme'
import { filterTrendDataByMaxDate } from '../../../utils/trendDateUtils'

interface TrendChartProps {
    data: {
        date: string
        count: number
    }[]
    previousTrendData?: Array<{ date: string; count: number }>
    comparisonWindowDays?: number
    maxDate?: string | null
}

const MAX_X_AXIS_TICK_COUNT = 10

const calculateAverageCount = (
    items: Array<{ date: string; count: number }>
): number =>
    items.length > 0
        ? items.reduce((sum, item) => sum + item.count, 0) / items.length
        : 0

const buildTrailingWindow = (
    items: Array<{ date: string; count: number }>,
    windowDays: number
): Array<{ date: string; count: number }> => {
    const trailingItems = items.slice(-windowDays)

    if (trailingItems.length >= windowDays) {
        return trailingItems
    }

    return [
        ...Array.from({ length: windowDays - trailingItems.length }, (_, index) => ({
            date: `empty-${index}`,
            count: 0,
        })),
        ...trailingItems,
    ]
}

const buildDateTicks = (
    items: Array<{ date: string; count: number }>
): string[] | undefined => {
    if (items.length === 0) {
        return undefined
    }

    if (items.length <= MAX_X_AXIS_TICK_COUNT) {
        return items.map((item) => item.date)
    }

    const lastIndex = items.length - 1
    const tickIndexes = Array.from(
        { length: MAX_X_AXIS_TICK_COUNT },
        (_, index) =>
            Math.round((index * lastIndex) / (MAX_X_AXIS_TICK_COUNT - 1))
    )

    return Array.from(new Set(tickIndexes)).map((index) => items[index].date)
}

export const TrendChart: React.FC<TrendChartProps> = ({
    data,
    previousTrendData,
    comparisonWindowDays,
    maxDate,
}) => {
    const { t } = useTranslation('statistics')
    const [viewMode, setViewMode] = useState<ChartViewMode>('area')
    const displayData = useMemo(
        () => filterTrendDataByMaxDate(data, maxDate),
        [data, maxDate]
    )
    const trendWindowDays = Math.max(
        1,
        comparisonWindowDays ?? Math.min(7, Math.floor(displayData.length / 2) || 1)
    )
    const xAxisTicks = useMemo(() => buildDateTicks(displayData), [displayData])

    const statistics = useMemo(() => {
        if (displayData.length === 0) {
            return { total: 0, avg: 0, trend: null, max: { date: '', count: 0 } }
        }

        const { total, max } = calculateStatistics(displayData, 'count')
        const startDate = dayjs(displayData[0].date)
        const endDate = dayjs(displayData[displayData.length - 1].date)
        const totalDays = endDate.diff(startDate, 'day') + 1
        const avg = totalDays > 0 ? total / totalDays : 0

        if (!previousTrendData) {
            return { total, avg, trend: null, max }
        }

        const recentData = buildTrailingWindow(displayData, trendWindowDays)
        const previousData = buildTrailingWindow(previousTrendData, trendWindowDays)
        const recentAverage = calculateAverageCount(recentData)
        const previousAverage = calculateAverageCount(previousData)
        const trend =
            previousAverage > 0
                ? calculateTrend(recentData, previousData, 'count')
                : recentAverage > 0
                  ? 100
                  : 0

        return { total, avg, trend, max }
    }, [displayData, previousTrendData, trendWindowDays])

    const extraControls = (
        <div style={{ display: 'inline-block' }}>
            <ChartViewSwitcher viewMode={viewMode} onChange={setViewMode} />
        </div>
    )

    const recentTrendTitle = (
        <span>
            {t('charts.trend.recentTrend', { days: trendWindowDays })}
            <Tooltip title={t('charts.trend.recentTrendTooltip')}>
                <InfoCircleOutlined style={{ marginLeft: 4 }} />
            </Tooltip>
        </span>
    )
    const hasTrend = statistics.trend !== null
    const trendValue = statistics.trend
    const trendIcon =
        trendValue === null ? undefined : trendValue >= 0 ? (
            <RiseOutlined />
        ) : (
            <FallOutlined />
        )
    const trendColor =
        trendValue === null
            ? undefined
            : trendValue >= 0
              ? ChartTheme.colors.trend.positive
              : ChartTheme.colors.trend.negative

    const peakDate =
        statistics.max && statistics.max.count > 0 ? statistics.max.date : '--'

    const statsCards = displayData.length > 0 && (
        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
            <Col xs={24} sm={12} lg={8} xl={5}>
                <Card size="small">
                    <Statistic
                        title={t('charts.trend.totalDetections')}
                        value={statistics.total}
                        precision={0}
                        suffix={t('charts.trend.countUnit')}
                    />
                </Card>
            </Col>
            <Col xs={24} sm={12} lg={8} xl={5}>
                <Card size="small">
                    <Statistic
                        title={t('charts.trend.averageDetections')}
                        value={statistics.avg}
                        precision={1}
                        suffix={t('charts.trend.perDay')}
                    />
                </Card>
            </Col>
            <Col xs={24} sm={12} lg={8} xl={5}>
                <Card size="small">
                    <Statistic
                        title={t('charts.trend.peakCount')}
                        value={statistics.max?.count ?? 0}
                        precision={0}
                        suffix={t('charts.trend.countUnit')}
                    />
                </Card>
            </Col>
            <Col xs={24} sm={12} lg={8} xl={5}>
                <Card size="small">
                    <Statistic
                        title={t('charts.trend.peakDate')}
                        value={peakDate}
                    />
                </Card>
            </Col>
            <Col xs={24} sm={12} lg={8} xl={4}>
                <Card size="small">
                    <Statistic
                        title={recentTrendTitle}
                        value={trendValue ?? ''}
                        precision={hasTrend ? 1 : undefined}
                        prefix={trendIcon}
                        valueStyle={{
                            color: trendColor,
                        }}
                        suffix={hasTrend ? '%' : undefined}
                    />
                </Card>
            </Col>
        </Row>
    )

    return (
        <BaseChartContainer
            title={t('charts.trend.title')}
            tooltip={t('charts.trend.tooltip')}
            containerSelector=".trend-chart-container"
            fileNamePrefix={t('charts.trend.fileNamePrefix')}
            extraActions={extraControls}
            rawData={displayData}
            csvHeaders={[
                t('charts.trend.csv.date'),
                t('charts.trend.csv.count'),
            ]}
            showCsvExport={false}
        >
            {statsCards}

            <ResponsiveContainer height={ChartTheme.responsiveHeight.default}>
                <ComposedChart data={displayData} margin={ChartTheme.margin.withBothLabels}>
                    <CartesianGrid
                        strokeDasharray={ChartTheme.axisStyle.strokeDasharray}
                        stroke={ChartTheme.axisStyle.stroke}
                    />
                    <XAxis
                        dataKey="date"
                        label={{
                            value: t('charts.trend.dateAxis'),
                            position: 'insideBottomRight',
                            offset: -10,
                        }}
                        scale="point"
                        ticks={xAxisTicks}
                    />
                    <YAxis
                        label={{
                            value: t('charts.trend.countAxis'),
                            angle: -90,
                            position: 'insideLeft',
                            offset: 15,
                        }}
                    />
                    <RechartsTooltip
                        formatter={(value) => [
                            `${value} ${t('charts.trend.countUnit')}`,
                            t('charts.trend.countLabel'),
                        ]}
                        labelFormatter={(label) =>
                            `${t('charts.trend.dateLabel')}: ${label}`
                        }
                        contentStyle={ChartTheme.tooltipStyle}
                    />
                    <Legend />

                    {viewMode === 'area' && (
                        <Area
                            type="monotone"
                            dataKey="count"
                            name={t('charts.trend.series.count')}
                            stroke={ChartTheme.colors.trend.line}
                            fill={ChartTheme.colors.trend.area}
                            strokeWidth={3}
                            activeDot={{ r: 6 }}
                            isAnimationActive={ChartTheme.animation.default.isAnimationActive}
                            animationBegin={ChartTheme.animation.default.animationBegin}
                            animationDuration={ChartTheme.animation.default.animationDuration}
                            animationEasing="ease-out"
                        />
                    )}
                    {viewMode === 'line' && (
                        <Line
                            type="monotone"
                            dataKey="count"
                            name={t('charts.trend.series.count')}
                            stroke={ChartTheme.colors.trend.line}
                            strokeWidth={3}
                            activeDot={{ r: 6 }}
                            isAnimationActive={ChartTheme.animation.default.isAnimationActive}
                            animationBegin={ChartTheme.animation.default.animationBegin}
                            animationDuration={ChartTheme.animation.default.animationDuration}
                            animationEasing="ease-out"
                        />
                    )}
                    {viewMode === 'bar' && (
                        <Bar
                            dataKey="count"
                            name={t('charts.trend.series.count')}
                            fill={ChartTheme.colors.trend.line}
                            isAnimationActive={ChartTheme.animation.default.isAnimationActive}
                            animationBegin={ChartTheme.animation.default.animationBegin}
                            animationDuration={ChartTheme.animation.default.animationDuration}
                            animationEasing="ease-out"
                        />
                    )}
                </ComposedChart>
            </ResponsiveContainer>
        </BaseChartContainer>
    )
}
